import type { JSX } from 'react';
import { usePref } from '../lib/prefs.js';
import { SettingsRow } from '../components/Settings.js';
import { SettingsMenu } from '../components/SettingsControls.js';
import { unmetRequirement } from './assertBinding.js';
import type { ViewBinding, ViewLevel, ViewType } from './contract.js';
import { forgetScreenSession, pinnedRecordViewPrefKey, pinnedViewPrefKey } from './useScreen.js';

/**
 * Ligne de reglage "vue par defaut" d'un ecran, fournie par le SOCLE.
 *
 * Elle vit dans le module `settings.tsx` de l'app, monte par le hub Parametres
 * du shell. Le but est qu'aucune app ne recode ce reglage : les 40 apps a venir
 * posent la meme ligne, avec les memes mots et le meme comportement.
 *
 * Deux regimes, et c'est tout le sujet :
 * - vide (le defaut) : "Derniere vue utilisee", la memoire automatique du core.
 *   L'ecran rouvre sur ce que l'utilisateur regardait la derniere fois ;
 * - une vue choisie : elle est EPINGLEE. L'ecran rouvre toujours dessus, meme
 *   si l'utilisateur en a regarde une autre entre-temps.
 *
 * DEUX NIVEAUX, deux lignes, deux clefs (`level`) :
 * - `collection` (le defaut) : la vue d'arrivee de la porte, parmi les vues de
 *   COLLECTION (une fiche porte un element precis, elle n'est pas une vue
 *   d'arrivee de collection). Clef `pinnedViewPrefKey`.
 * - `record` (chantier `fiche-suivi`, « Ouvrir les fiches sur ») : la vue dans
 *   laquelle s'ouvre une fiche de la porte, parmi ses vues de FICHE. Vide : le
 *   defaut de la porte (son `auClic` au Studio, sinon « Fiche »). Clef
 *   `pinnedRecordViewPrefKey`, relue par le moteur a l'ouverture d'une fiche.
 *   Les deux se choisissent independamment.
 *
 * ET SEULES LES VUES REELLEMENT NOURRIES SONT CHOISISSABLES : quand l'ecran
 * fournit ses `bindings`, une vue au role manquant (celle que le selecteur de
 * la zone 3 grisera) reste LISTEE mais grisee ici aussi, avec sa raison dans
 * le menu. Jamais absente : "pourquoi mon kanban n'est pas propose" ne doit
 * pas devenir un bug silencieux ; jamais epinglable : epingler une vue que
 * l'ecran grise ouvrirait l'app sur une impasse.
 *
 * Une ligne par porte quand `doorId` est fourni, sinon par ecran.
 * Chaque cible utilise son propre catalogue de vues.
 *
 * @example
 * <SettingsSection title="Mon affichage">
 *   <ViewDefaultSetting
 *     appId="contact"
 *     screenId="contacts"
 *     types={CONTACT_VIEW_TYPES}
 *     bindings={CONTACT_BINDINGS}
 *   />
 * </SettingsSection>
 */
export interface ViewDefaultSettingProps<T> {
  /** Id de l'app, identique a celui passe a `useScreen`. */
  appId: string;
  /** Id de l'ecran, identique a celui passe a `useScreen`. */
  screenId: string;
  /** Porte visee. Absent : preference historique de l’ecran sans portes. */
  doorId?: string;
  /** Le catalogue de vues de CET ecran, le meme tableau que `useScreen`. */
  types: ViewType[];
  /**
   * Les bindings de CET ecran, les memes que ceux passes a `useScreen`.
   * Avec eux, une vue non nourrie (role manquant) est grisee avec sa raison,
   * au lieu d'etre epinglable puis grisee par l'ecran. Sans eux, toutes les
   * vues collection restent proposees (comportement historique).
   */
  bindings?: ReadonlyArray<ViewBinding<T>>;
  /**
   * Le niveau regle : `collection` (defaut, la vue d'arrivee de la porte) ou
   * `record` (la vue dans laquelle s'ouvrent ses fiches).
   */
  level?: ViewLevel;
  /** Libelle de la ligne. Defaut : "Vue par défaut", ou "Ouvrir les fiches sur" au niveau `record`. */
  label?: string;
  /** Un ETAT sous le libelle, une ligne, jamais une explication ni la portee (G11). */
  hint?: string;
}

/** La valeur vide du menu : la memoire automatique (collection), le defaut de la porte (fiche). */
const LAST_USED = '';

/** Les mots de chaque niveau : le libelle de la ligne et celui de la valeur vide. */
const MOTS_DU_NIVEAU: Readonly<Record<ViewLevel, { label: string; vide: string }>> = {
  collection: { label: 'Vue par défaut', vide: 'Dernière vue utilisée' },
  record: { label: 'Ouvrir les fiches sur', vide: 'Défaut de la porte' },
};

/**
 * La raison d'un grisage, reduite a son CONSTAT : `unmetRequirement` parle au
 * developpeur en deux temps (le constat, puis la consigne "ajoute..."), une
 * option de liste n'a la place que du premier. On coupe donc a la premiere
 * phrase, puis avant la consigne quand elle est accolee par " : ajoute".
 * Si la formulation des messages change, la coupe degrade sans casser : le
 * texte complet de la raison reste affiche.
 */
function shortReason(reason: string): string {
  const sentence = reason.split('. ')[0] ?? reason;
  const constat = (sentence.split(' : ajoute')[0] ?? sentence).replace(/\.$/, '');
  return constat.charAt(0).toLowerCase() + constat.slice(1);
}

export function ViewDefaultSetting<T = never>({
  appId,
  screenId,
  doorId,
  types,
  bindings,
  level = 'collection',
  label = MOTS_DU_NIVEAU[level].label,
  hint,
}: ViewDefaultSettingProps<T>): JSX.Element {
  const prefKey = level === 'record' ? pinnedRecordViewPrefKey : pinnedViewPrefKey;
  const [pinned, setPinned] = usePref<string | null>(prefKey(appId, screenId, doorId), null);

  // Les vues DU NIVEAU regle.
  const duNiveau = types.filter((type) => type.level === level);

  /** Pourquoi cette vue serait grisee par l'ecran, ou null si elle est nourrie. */
  function reasonOf(type: ViewType): string | null {
    if (bindings == null) return null;
    return unmetRequirement(
      type,
      bindings.find((candidate) => candidate.type === type.id),
    );
  }

  // Une pref qui designe une vue disparue du catalogue est ignoree, jamais
  // affichee : l'app a pu retirer une vue depuis que l'utilisateur a choisi.
  // Une pref qui designe une vue devenue non nourrie reste affichee (l'option
  // grisee peut etre l'option courante) : la verite d'abord, et en changer
  // reste possible.
  const value = duNiveau.some((type) => type.id === pinned) ? String(pinned) : LAST_USED;

  function choose(next: string): void {
    setPinned(next === LAST_USED ? null : next);
    // Oublie la reprise de cette cible, notamment la fiche ouverte :
    // le prochain montage applique le choix de vue tout juste pose.
    forgetScreenSession(appId, screenId, doorId);
  }

  if (duNiveau.length <= 1) {
    return (
      <SettingsRow label={label} hint={hint}>
        <span className="text-[0.8125rem] text-ink-400">
          {duNiveau[0]?.label ?? 'Aucune vue'}
        </span>
      </SettingsRow>
    );
  }

  return (
    <SettingsRow label={label} hint={hint}>
      <SettingsMenu
        value={value}
        onChange={choose}
        label={level === 'record' ? `Ouvrir les fiches de ${doorId ?? screenId}` : `Vue par défaut de ${doorId ?? screenId}`}
        options={[
          { value: LAST_USED, label: MOTS_DU_NIVEAU[level].vide },
          ...duNiveau.map((type) => {
            const reason = reasonOf(type);
            return reason != null
              ? { value: type.id, label: type.label, detail: shortReason(reason), inactif: reason }
              : { value: type.id, label: type.label };
          }),
        ]}
      />
    </SettingsRow>
  );
}
